import { Box, Divider, Slide, Tooltip, Typography } from '@mui/material';
import { RichTreeView, TreeItem2, TreeItem2Props } from '@mui/x-tree-view';
import React, { Dispatch, RefObject, SetStateAction, SyntheticEvent } from 'react';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';
import { EntityAccess, EntityType, PermissionContext, PermissionContextMapping, Permissions } from '@lanterds/types';
import { DealerHierarchyItem, SelectedDealer, Quantity, SelectedCustomer } from '../types';
import ClearIcon from '@mui/icons-material/Clear';

type Props = {
    setSelectedDealers: Dispatch<SetStateAction<SelectedDealer[]>>;
    selectedDealers: SelectedDealer[];
    dealers: DealerHierarchyItem[];
    isDealersFetching?: boolean;
    containerRef: RefObject<HTMLElement>;
    isVisible: boolean;
    selectedCustomers: SelectedCustomer[];
    adminsPermissions: Partial<Record<Permissions, boolean | EntityAccess[]>>;
};

export default function CustomerFlowTile({
    setSelectedDealers,
    selectedDealers,
    dealers,
    isDealersFetching,
    containerRef,
    isVisible,
    selectedCustomers,
    adminsPermissions,
}: Props) {
    const dealerSelectionHandler = (event: SyntheticEvent<Element, Event>, selectedIds: string[]) => {
        const newSelectedDealersList: SelectedDealer[] = [];

        if (selectedIds[0] === Quantity.all) {
            setSelectedDealers([{ id: Quantity.all, hierarchyId: Quantity.all, type: EntityType.DEALER } as SelectedDealer]);
        } else {
            for (const id of selectedIds) {
                const parts = id.split('_');
                const customerId = parts[0];
                const dealerCode = parts[1];

                if (!dealerCode) continue;

                // Getting the parent node of DealerHierarchyItems, which is a customer
                const customer = dealers.find((customer) => customer.hierarchyId === customerId);
                // Children of the DealerHierarchyItems are the dealers
                const dealer = customer?.children?.find((dealer: DealerHierarchyItem) => dealer.hierarchyId === id);
                if (customer && dealer) {
                    newSelectedDealersList.push({ ...dealer, id: dealer.id, type: EntityType.DEALER });
                }
            }
            setSelectedDealers(newSelectedDealersList);
        }
    };

    const canAssignAllDealers = () => {
        let hasAccess = true;

        for (const customer of selectedCustomers) {
            let foundDealerInPermission = false;

            for (const permissionName of Object.keys(adminsPermissions)) {
                if (PermissionContextMapping[permissionName as Permissions] === PermissionContext.DEALER) {
                    const entities = adminsPermissions[permissionName as Permissions];

                    if (entities && Array.isArray(entities)) {
                        const isCustomerFound = !!entities?.find((entity) => {
                            if (
                                entity.type === EntityType.ACCESS_ALL_CUSTOMERS ||
                                (entity.type === EntityType.CUSTOMER && entity.id === customer.customerId)
                            ) {
                                return true;
                            }
                            return false;
                        });

                        if (isCustomerFound) {
                            foundDealerInPermission = true;
                            break;
                        }
                    }
                }
            }

            if (!foundDealerInPermission) {
                hasAccess = false;
                break;
            }
        }

        return hasAccess;
    };

    const isDealerDisabledHandler = (item: DealerHierarchyItem) => {
        const { hierarchyId } = item;

        if (!selectedDealers.length) {
            if (hierarchyId !== Quantity.all || (hierarchyId === Quantity.all && canAssignAllDealers())) {
                return false;
            }
        } else {
            if (
                (hierarchyId === Quantity.all && selectedDealers[0].id === Quantity.all) ||
                (hierarchyId !== Quantity.all && selectedDealers[0].id !== Quantity.all)
            ) {
                return false;
            }
        }

        return true;
    };

    const DealerTreeItem = (props: TreeItem2Props) => {
        const { itemId, children } = props;
        let toolTipTitle = '';

        if (itemId === Quantity.all && !canAssignAllDealers()) {
            toolTipTitle = 'Based on your customer selections, you do not have access to assign All Dealers';
        }

        return (
            <Tooltip arrow title={toolTipTitle}>
                {/* Need to wrap in block element or else TreeItem2 throws a warning from the tooltip */}
                <Box>
                    <TreeItem2
                        {...props}
                        className={`className ${
                            (itemId && itemId.split('_')[1] === Quantity.none) ||
                            (children && Array.isArray(children) && children.length)
                                ? 'hideCheckBox'
                                : ''
                        }`}
                    />
                </Box>
            </Tooltip>
        );
    };

    const ClearSelectionsButton = () => {
        return (
            <Typography onClick={() => setSelectedDealers([])} className="clear-button" component="div">
                <ClearIcon className="clear-icon" /> Clear
            </Typography>
        );
    };

    return (
        <Box
            className="slide-container"
            sx={{
                display: isVisible ? 'flex' : 'none',
            }}
        >
            <Slide in={isVisible} direction={'right'} container={containerRef.current} unmountOnExit>
                <Box className="container dealer-container">
                    <Box className="header-container">
                        <Typography variant="h6" component="div" className="header">
                            Dealers
                        </Typography>
                        {selectedDealers.length ? <ClearSelectionsButton /> : <></>}
                    </Box>
                    <Divider />

                    <Box className="content-container">
                        {isDealersFetching ? (
                            <Box className="loading-container">
                                <LoadingIndicator />
                            </Box>
                        ) : (
                            <RichTreeView
                                multiSelect={true}
                                id="dealer-tree-view"
                                getItemLabel={(item: DealerHierarchyItem) => item.displayName}
                                getItemId={(item: DealerHierarchyItem) => item.hierarchyId}
                                items={dealers}
                                slots={{ item: DealerTreeItem }}
                                onSelectedItemsChange={dealerSelectionHandler}
                                selectedItems={selectedDealers.flatMap((dealer) => dealer.hierarchyId)}
                                checkboxSelection={true}
                                isItemDisabled={isDealerDisabledHandler}
                            />
                        )}
                    </Box>
                </Box>
            </Slide>
        </Box>
    );
}
